import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, X, Share, Plus } from "lucide-react";

/**
 * PWA install prompt.
 *
 * Behaviour:
 *  • Chrome/Edge/Android: catches `beforeinstallprompt` and shows a custom banner
 *    that calls `prompt()` on click.
 *  • iOS Safari: shows manual "tap Share → Add to Home Screen" instructions
 *    (Apple does not support the beforeinstallprompt event).
 *  • Hidden once already installed (display-mode: standalone) or after the user
 *    dismisses it for the current session.
 */
export default function InstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [show, setShow] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;

    // Already installed → never show
    const standalone =
      window.matchMedia?.("(display-mode: standalone)").matches ||
      window.navigator.standalone === true;
    if (standalone) return;

    // Respect a previous dismiss for this session
    try {
      if (sessionStorage.getItem("db-install-dismissed") === "1") return;
    } catch (e) { /* sessionStorage blocked — ignore */ }

    // Detect iOS Safari (no beforeinstallprompt support)
    const ua = window.navigator.userAgent || "";
    const iOS =
      /iPad|iPhone|iPod/.test(ua) &&
      !window.MSStream &&
      !/CriOS|FxiOS|EdgiOS/.test(ua); // exclude Chrome/Firefox/Edge on iOS
    if (iOS) {
      // Wait a few seconds before showing so we don't crowd the initial paint
      const t = setTimeout(() => { setIsIOS(true); setShow(true); }, 4000);
      return () => clearTimeout(t);
    }

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferred(e);
      setShow(true);
    };
    window.addEventListener("beforeinstallprompt", onBeforeInstall);

    const onInstalled = () => { setShow(false); setDeferred(null); };
    window.addEventListener("appinstalled", onInstalled);

    return () => {
      window.removeEventListener("beforeinstallprompt", onBeforeInstall);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const dismiss = () => {
    setShow(false);
    try { sessionStorage.setItem("db-install-dismissed", "1"); } catch (e) { /* ignore */ }
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setShow(false);
      }
    } catch (e) {
      console.debug("install prompt failed:", e);
    } finally {
      setDeferred(null);
    }
  };

  if (!show) return null;

  return (
    <div
      data-testid="pwa-install-banner"
      className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[60] animate-in slide-in-from-bottom-4 fade-in duration-300"
      role="dialog"
      aria-label="Install DeliveryBoss"
    >
      <div className="rounded-xl border border-cyan-400/30 bg-slate-950/95 backdrop-blur-md shadow-2xl shadow-cyan-500/10 p-4 flex gap-3 items-start">
        <div className="shrink-0 w-10 h-10 rounded-lg bg-cyan-400/10 grid place-items-center">
          <img
            src="/logo/deliveryboss-day.png"
            alt=""
            className="w-7 h-7"
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-white">
            Install DeliveryBoss
          </div>
          {isIOS ? (
            <div className="text-xs text-slate-300 mt-1 leading-relaxed">
              Tap{" "}
              <Share className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5 text-cyan-400" />{" "}
              <span className="font-medium text-cyan-300">Share</span>, then{" "}
              <Plus className="inline w-3.5 h-3.5 mx-0.5 -mt-0.5 text-cyan-400" />{" "}
              <span className="font-medium text-cyan-300">Add to Home Screen</span>.
            </div>
          ) : (
            <>
              <div className="text-xs text-slate-300 mt-1 leading-relaxed">
                Get the app on your home screen — faster, full-screen, works offline-ready.
              </div>
              <div className="mt-3 flex gap-2">
                <Button
                  data-testid="pwa-install-btn"
                  size="sm"
                  onClick={install}
                  className="bg-cyan-400 text-slate-950 hover:bg-cyan-300 font-medium h-8 px-3"
                >
                  <Download className="w-3.5 h-3.5 mr-1.5" /> Install
                </Button>
                <Button
                  data-testid="pwa-install-later"
                  size="sm"
                  variant="ghost"
                  onClick={dismiss}
                  className="text-slate-400 hover:text-white hover:bg-slate-800 h-8 px-2"
                >
                  Later
                </Button>
              </div>
            </>
          )}
        </div>
        <button
          data-testid="pwa-install-close"
          onClick={dismiss}
          aria-label="Dismiss"
          className="shrink-0 -mr-1 -mt-1 p-1 text-slate-500 hover:text-slate-200 rounded transition-colors"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
